import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Card from "../../../../Components/Card/Card";
import customerActions from "../../../../Actions/customerActions";
import commonActions from "../../../../Actions/commonActions";
import VonEditEmployeeDetailsForm from "../../../../Components/Customer/VON/EditEmployee/VonEditEmployeeDetailsForm";
import VonEditAccessRightsForm from "../../../../Components/Customer/VON/EditEmployee/VonEditAccessRightsForm";
import VonEditEmployeeTypeForm from "../../../../Components/Customer/VON/EditEmployee/VonEditEmployeeTypeForm";
import { VonAccessRights } from "../../../../Constants/VON/EmployeeForm/vonAccessRights";
import VonEditEmployeeRolesForm from "../../../../Components/Customer/VON/EditEmployee/VonEditEmployeeRolesForm";
import AlertSmall from "../../../../Components/AlertSmall";
import { UserRoles } from "../../../../Constants/userRoles";
import VonEditEmployeeUsernameForm from "../../../../Components/Customer/VON/EditEmployee/VonEditEmployeeUsernameForm";
import customerRoutes from "../../../../Constants/Customer/customerRoutes";
import useUserAccess from "../../../../Hooks/useUserAccess";
import employeesActions from "../../../../Actions/Von/employeesActions";
import {VonEmployeeTypes} from "../../../../Constants/VON/EmployeeForm/vonEmployeeTypes";

const VonEditEmployeeFormContainer = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const { hasReadWriteAccess } = useUserAccess();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const editVonEmployeeForm = useSelector(state => state.CommonReducer.forms?.editVonEmployeeForm);
    const editVonEmployeeRolesForm = useSelector(state => state.CommonReducer.forms?.editVonEmployeeRolesForm);
    const editVonEmployeeEmailForm = useSelector(state => state.CommonReducer.forms?.editVonEmployeeEmailForm);
    const vonAccounts = useSelector(state => state.CustomerReducer.vonAccounts);
    const vonEmployeeTypeRestrictions = useSelector(state => state.CustomerReducer.vonEmployeeTypeRestrictions);
    const customer = useSelector(state => state.CustomerReducer.customer);
    const vonProducts = useSelector(state => state.CustomerReducer.vonProducts);
    const vonProductsLoading = useSelector(state => state.CommonReducer.dataLoading.vonProducts);
    const vonEmployeeForEditLoading = useSelector(state => state.CommonReducer.dataLoading.vonEmployeeForEdit);
    const vonAccount = vonAccounts?.find(account => account.id === id) || null;
    const hasBankruptcyTrustee = vonAccounts?.some(account => account.employeeType === 3);
    const isBankruptcyTrustee = vonAccount?.employeeType === 3;
    const hasOtherBankruptcyTrustees = vonAccounts?.some(account => account.id !== id && account.employeeType === 3);

    useEffect(() => {
        if (!vonProducts || !vonAccount) return;
        dispatch(employeesActions.getVonEmployeeForEdit(vonProducts, vonAccount));
    }, [vonProducts, vonAccount]);

    useEffect(() => {
        return () => {
            ['editVonEmployeeForm', 'editVonEmployeeRolesForm', 'editVonEmployeeEmailForm']
                .forEach(form => dispatch(commonActions.cancelFormEditing(form)));
        };
    }, []);

    const onChange = (form, value, field) => {
        dispatch(commonActions.changeMultiFormField(form, field, value));
    }

    const callback = () => {
        navigate(customerRoutes.customer(customer.pathId, customerRoutes.Users));
        dispatch(customerActions.getCustomerVonProducts(customer.vonCustomer.id ?? customer.vossCustomer.id));
        dispatch(customerActions.getVonAccounts(customer.vonCustomer.id ?? customer.vossCustomer.id));
        dispatch(customerActions.searchSubscriptions(customer.vonCustomer.id ?? customer.vossCustomer.id));
    }

    const onSubmit = () => {
        switch (editVonEmployeeForm.data.onlineRoleId) {
            case VonAccessRights.Delete:
                dispatch(employeesActions.deleteVonEmployee(id, () => callback()));
                break;
            case VonAccessRights.Inactive:
                dispatch(employeesActions.inactivateVonEmployee(id, () => callback()));
                break;
            default:
                dispatch(employeesActions.updateVonEmployee(editVonEmployeeForm, editVonEmployeeRolesForm, editVonEmployeeEmailForm, customer.vonCustomer.id ?? customer.vossCustomer.id, () => callback()));
                break;
        }
    }

    const isPrimaryContact = vonAccount?.isPrimaryContact;

    let isLoading = vonProductsLoading || vonEmployeeForEditLoading;
    let isChanged = editVonEmployeeEmailForm?.changed || editVonEmployeeForm?.changed || editVonEmployeeRolesForm?.changed;
    let disableRoles = editVonEmployeeForm && (editVonEmployeeForm.data.onlineRoleId === VonAccessRights.Delete || editVonEmployeeForm.data.onlineRoleId === VonAccessRights.Inactive);

    return <Card>
        <Card.Header>
            <Card.Header.Title>Editera användare</Card.Header.Title>
        </Card.Header>
        <Card.Content isLoading={isLoading}>
            <div className={`d-grid gap-24 ${!hasReadWriteAccess(UserRoles.CompanyUser) ? 'disabled ' : ''}`} >
                <div className="d-flex align-items-start">
                    <fieldset className="d-grid col gap-24 mb-0 pl-0">
                        {editVonEmployeeForm && <>
                            <VonEditEmployeeDetailsForm form={editVonEmployeeForm} onChange={onChange} isEditDisabled={hasBankruptcyTrustee && !isBankruptcyTrustee && editVonEmployeeForm.data.employeeTypeId != VonEmployeeTypes.BankruptcyTrustee} />
                            <VonEditEmployeeUsernameForm form={editVonEmployeeEmailForm} onChange={onChange} />
                            <VonEditEmployeeTypeForm form={editVonEmployeeForm} onChange={onChange} isBankruptcyTrustee={isBankruptcyTrustee} hasOtherBankruptcyTrustees={hasOtherBankruptcyTrustees} />
                            <VonEditAccessRightsForm form={editVonEmployeeForm} onChange={onChange} isBankruptcyTrustee={isBankruptcyTrustee} hasOtherBankruptcyTrustees={hasOtherBankruptcyTrustees} />
                        </>}
                        {isPrimaryContact && !isBankruptcyTrustee && <div>
                            <AlertSmall alert={{ type: "warning", title: t('vonaccountformcontainer.primarycontactalerttitle'), message: t('vonaccountformcontainer.primarycontactalertmessage') }} />
                        </div>}
                        {hasBankruptcyTrustee && !isBankruptcyTrustee && <div>
                            <AlertSmall alert={{ type: "warning", title: t('vonaccountformcontainer.maincontactalerttitle'), message: t('vonaccountformcontainer.maincontactalertmessage') }} />
                        </div>}
                    </fieldset>
                    <fieldset className={`d-grid col gap-24 mb-0 pl-0 ${disableRoles ? 'disabled' : ''}`}>
                        {editVonEmployeeRolesForm && <>
                            <VonEditEmployeeRolesForm vonAccount={vonAccount} form={editVonEmployeeRolesForm} vonProducts={vonProducts} onChange={onChange} vonAccounts={vonAccounts} vonEmployeeTypeRestrictions={vonEmployeeTypeRestrictions} />
                        </>}
                    </fieldset>
                </div>
            </div>
        </Card.Content>
        <Card.Footer>
            <Card.Footer.Buttons>
                <button className='btn btn-default' onClick={() => navigate(customerRoutes.customer(customer.pathId, customerRoutes.Users))}>{t('common.cancel')}</button>
                <button className='btn btn-default btn-primary' disabled={isLoading || !isChanged} onClick={() => onSubmit()}>{t('common.save')}</button>
            </Card.Footer.Buttons>
        </Card.Footer>
    </Card>
}

export default VonEditEmployeeFormContainer;